<script setup lang="ts">
import { PortableText, type PortableTextVueComponents } from '@portabletext/vue';
import type { PortableTextBlock } from '@sanity/types';
import { BLOCKS, LIST, MARKS, STYLE } from '@pkg/config/sanity/portableEditor';
import ExternalLinkRenderer from './portableEditor/marks/ExternalLinkRenderer.vue';
import DynamicStringRenderer from './portableEditor/marks/DynamicStringRenderer.vue';
import InternalLinkRenderer from './portableEditor/marks/InternalLinkRenderer.vue';
import PageAnchorRenderer from './portableEditor/marks/PageAnchorRenderer.vue';
import JavaScriptLinkRenderer from './portableEditor/marks/JavaScriptLink.vue';
import InternalTextLinkRenderer from './portableEditor/marks/InternalTextLinkRenderer.vue';
import ImageRenderer from './portableEditor/blocks/Image.vue';
import MarkupTableRenderer from './portableEditor/blocks/MarkupTable.vue';
import VideoRenderer from './portableEditor/blocks/Video.vue';
import SurpriseBagCardRenderer from './portableEditor/blocks/SurpriseBagCard.vue';
import UnknownBlock from './portableEditor/unknown/UnknownBlock.vue';
import BulletList from "./portableEditor/list/BulletList.vue";
import NumberedList from "./portableEditor/list/NumberedList.vue";
import TickList from "./portableEditor/list/TickList.vue";
import { slugify } from '@pkg/utilities/strings/slugify';
import { HEADING_CONFIGS } from '@pkg/config/sanity/portableEditorHeadings';
import { computed, h } from 'vue';

const props = defineProps<{
  content?: Array<PortableTextBlock> | string;
  alignment?: 'left' | 'center' | 'right';
  variant?: 'default' | 'base-title';
  headingContent?: boolean;
}>();

// Extract text from a block to generate anchor ID
const extractBlockText = (block: PortableTextBlock): string => {
  if (!block.children || !Array.isArray(block.children)) return '';
  return block.children
    .map((child: any) => child.text || '')
    .filter(Boolean)
    .join(' ')
    .trim();
};

// Generate anchor ID for all heading styles
const generateAnchorId = (block: PortableTextBlock): string | undefined => {
  const headingStyles = HEADING_CONFIGS.map(config => config.value);
  if (block.style && headingStyles.includes(block.style as string)) {
    const text = extractBlockText(block);
    return text ? slugify(text) : undefined;
  }
  return undefined;
};

// Helper to create heading components
const createHeading = (tag: string, className?: string) => {
  return (props: any, {slots}: any) => {
    const anchorId = generateAnchorId(props.value as PortableTextBlock);
    const attrs: Record<string, any> = { id: anchorId };
    if (className) attrs.class = className;
    return h(tag, attrs, slots.default?.());
  };
};

// Generate heading block components from shared configuration
const headingBlocks = Object.fromEntries(
  HEADING_CONFIGS.map(({ value, tag, className }) => [value, createHeading(tag, className)])
);

const titleLineBlock = ({ value }: any, { slots }: any) => {
  const block = value as PortableTextBlock;
  const extra = value.style === 'normal' ? undefined : HEADING_CONFIGS.find(c => c.value === value.style)?.className ?? value.style;
  const id = generateAnchorId(block);
  return h('span', {class: ["c-portable-editor__title-line", extra].filter(Boolean).join(" "), ...(id && {id})}, slots.default?.());
};

const components: Partial<PortableTextVueComponents> = {
  marks: {
    [MARKS.URL_LINK]: ExternalLinkRenderer,
    [MARKS.PAGE_LINK]: InternalLinkRenderer,
    [MARKS.PAGE_ANCHOR]: PageAnchorRenderer,
    [MARKS.JAVASCRIPT_LINK]: JavaScriptLinkRenderer,
    [MARKS.INTERNAL_TEXT_LINK]: InternalTextLinkRenderer,
    [MARKS.COLORED]: ({}, {slots}) => h('span', {class: "colored"}, slots.default?.()),
  },
  types: {
    [BLOCKS.DATA]: DynamicStringRenderer,
    [BLOCKS.IMAGE]: ImageRenderer,
    [BLOCKS.MARKUP_TABLE]: MarkupTableRenderer,
    [BLOCKS.VIDEO]: VideoRenderer,
    [BLOCKS.SURPRISE_BAG_CARD]: SurpriseBagCardRenderer,
  },
  list: {
    [LIST.BULLET]: BulletList,
    [LIST.NUMBER]: NumberedList,
    [LIST.TICK]: TickList,
  },
  unknownType: UnknownBlock,
  block: props.headingContent ? titleLineBlock : {
    ...headingBlocks,
    [STYLE.BODY_LARGE]: ({}, {slots}) => h('span', {class: "body-large"}, slots.default?.()),
    [STYLE.BODY_SMALL]: ({}, {slots}) => h('span', {class: "body-small"}, slots.default?.()),
    [STYLE.BODY_XSMALL]: ({}, {slots}) => h('span', {class: "body-xsmall"}, slots.default?.()),
  }
};

</script>

<template>
  <template v-if="headingContent">
    <PortableText
      v-if="props.content && typeof props.content !== 'string'"
      :value="props.content || []"
      :components="components"
    />
    <template v-else>
      {{ props.content }}
    </template>
  </template>
  <div
    v-else
    class="c-portable-editor"
    :class="{ 'c-portable-editor--base-title': props.variant === 'base-title' }"
    :style="props.alignment ? `text-align: ${props.alignment}` : ''"
  >
    <PortableText
      v-if="props.content && typeof props.content !== 'string'"
      :value="props?.content || []"
      :components="components"
    />
    <p v-else>
      {{ props.content }}
    </p>
  </div>

</template>

<style scoped>
@import './styles/portableEditor.pcss';

.c-portable-editor--base-title {
  :deep(p) {
    margin-bottom: 0 !important;
    margin-top: 0 !important;
  }
}

:deep(.c-portable-editor__title-line) {
  display: block;
}
</style>
